import { createClient, type SupabaseClient, type User } from '@supabase/supabase-js';
import type { SaveFile } from '../model/types';
import { RemoteError, remoteErrorFromMessage, type AuthUser, type FeedbackInsert, type RemoteApi } from './remoteApi';
import type { SupabasePublicConfig } from './supabaseConfig';

export function createSupabaseRemote(config: SupabasePublicConfig): RemoteApi {
  const supabase = createClient(config.url, config.key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
  });
  return new SupabaseRemote(supabase, config);
}

class SupabaseRemote implements RemoteApi {
  constructor(
    private readonly supabase: SupabaseClient,
    private readonly config: SupabasePublicConfig,
  ) {}

  async currentUser(): Promise<AuthUser | null> {
    const { data, error } = await this.supabase.auth.getSession();
    if (error) throw remoteErrorFromMessage(error.message);
    return toUser(data.session?.user ?? null);
  }

  async signInAnonymously(): Promise<AuthUser> {
    const { data, error } = await this.supabase.auth.signInAnonymously();
    if (error || !data.user) throw remoteErrorFromMessage(error?.message ?? 'not_authenticated');
    return toUser(data.user) as AuthUser;
  }

  async linkGoogle(redirectTo: string): Promise<void> {
    const { error } = await this.supabase.auth.linkIdentity({
      provider: 'google',
      options: { redirectTo },
    });
    if (error) throw remoteErrorFromMessage(error.message);
  }

  async signInWithGoogle(redirectTo: string): Promise<void> {
    const { error } = await this.supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
    if (error) throw remoteErrorFromMessage(error.message);
  }

  async signOut(): Promise<void> {
    const { error } = await this.supabase.auth.signOut();
    if (error) throw remoteErrorFromMessage(error.message);
  }

  async rpc<T>(name: string, args: Record<string, unknown>): Promise<T> {
    const { data, error } = await this.supabase.rpc(name, args);
    if (error) throw remoteErrorFromMessage(error.message);
    return data as T;
  }

  async invoke<T>(name: string, body: Record<string, unknown>, options?: { keepalive?: boolean }): Promise<T> {
    const { data: sessionData, error: sessionError } = await this.supabase.auth.getSession();
    if (sessionError) throw remoteErrorFromMessage(sessionError.message);
    const token = sessionData.session?.access_token;
    if (!token) throw new RemoteError('not_authenticated');
    let response: Response;
    try {
      response = await fetch(`${this.config.url}/functions/v1/${name}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: this.config.key,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        keepalive: options?.keepalive === true,
      });
    } catch (error) {
      throw remoteErrorFromMessage(error instanceof Error ? error.message : 'network');
    }
    const payload = (await response.json().catch(() => null)) as { error?: string; retry_after_ms?: unknown } | null;
    if (!response.ok) {
      const remote = remoteErrorFromMessage(payload?.error ?? `function_${response.status}`);
      if (remote.code === 'too_fast' && typeof payload?.retry_after_ms === 'number' && Number.isInteger(payload.retry_after_ms)) {
        throw new RemoteError('too_fast', 'too_fast', payload.retry_after_ms);
      }
      throw remote;
    }
    return payload as T;
  }

  async insertFeedback(row: FeedbackInsert): Promise<void> {
    const { error } = await this.supabase.from('feedback_reports').insert(row);
    if (error) throw remoteErrorFromMessage(error.message);
  }

  async uploadFeedbackShot(path: string, bytes: Uint8Array): Promise<void> {
    const { error } = await this.supabase.storage.from('feedback').upload(path, bytes, {
      contentType: 'image/jpeg',
      upsert: false,
    });
    if (error) throw remoteErrorFromMessage(error.message);
  }

  async attachFeedbackScreenshot(reportId: string): Promise<void> {
    await this.rpc('attach_feedback_screenshot', { report_id: reportId });
  }

  async readSave(): Promise<SaveFile | null> {
    const { data, error } = await this.supabase.from('saves').select('version, data').maybeSingle();
    if (error) throw remoteErrorFromMessage(error.message);
    if (!data) return null;
    return { version: Number(data.version), data: data.data as SaveFile['data'] };
  }
}

function toUser(user: User | null): AuthUser | null {
  if (!user) return null;
  const provider = (user.app_metadata as { provider?: string } | undefined)?.provider;
  return {
    id: user.id,
    isAnonymous: user.is_anonymous === true || provider === 'anonymous',
    email: user.email ?? null,
  };
}
